import { getMomentTz } from "@dexteel/mesf-core";
import {
	Box,
	Divider,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import type { Duration } from "moment";
import "moment-duration-format";
import { useMemo } from "react";
import { DelayByShift } from "../models/DelayByShift";

const moment = getMomentTz();

type CategorySummary = {
	name: string;
	color: string;
	durationMin: number;
	percentage: number;
};

type DelayCodeSummary = {
	name: string;
	durationMin: number;
	percentage: number;
};

export const DelaysSummary = ({
	delays,
	sectionName,
}: {
	delays: DelayByShift[];
	sectionName?: string | null;
}) => {
	const summary = useMemo(() => {
		const totalDelays = delays.length;
		const totalDurationSec = delays.reduce(
			(sum, d) => sum + (d.Duration || 0),
			0,
		);
		const totalDurationMin = totalDurationSec / 60;
		const avgDelayDurationSec =
			totalDelays > 0 ? totalDurationSec / totalDelays : 0;

		const categoryMap = new Map<string, { color: string; duration: number }>();
		for (const delay of delays) {
			const catName = delay.DelayCategoryName || "Undeclared";
			const catColor = delay.DelayCategoryColor || "#9e9e9e";
			const existing = categoryMap.get(catName);
			if (existing) existing.duration += delay.Duration || 0;
			else
				categoryMap.set(catName, {
					color: catColor,
					duration: delay.Duration || 0,
				});
		}

		const byCategory: CategorySummary[] = Array.from(categoryMap.entries())
			.map(([name, data]) => ({
				name,
				color: data.color,
				durationMin: data.duration / 60,
				percentage:
					totalDurationSec > 0 ? (data.duration / totalDurationSec) * 100 : 0,
			}))
			.sort((a, b) => b.durationMin - a.durationMin);

		const delayCodeMap = new Map<string, number>();
		for (const delay of delays) {
			const codeName = delay.DelayCodeName || "Undeclared";
			delayCodeMap.set(
				codeName,
				(delayCodeMap.get(codeName) || 0) + (delay.Duration || 0),
			);
		}

		const topDelayCodes: DelayCodeSummary[] = Array.from(delayCodeMap.entries())
			.map(([name, duration]) => ({
				name,
				durationMin: duration / 60,
				percentage:
					totalDurationSec > 0 ? (duration / totalDurationSec) * 100 : 0,
			}))
			.sort((a, b) => b.durationMin - a.durationMin)
			.slice(0, 8);

		return {
			totalDelays,
			totalDurationSec,
			totalDurationMin,
			avgDelayDurationSec,
			byCategory,
			topDelayCodes,
		};
	}, [delays]);

	const maxCategoryMin = summary.byCategory[0]?.durationMin || 1;
	const kpis = [
		{ label: "Total Delays", value: String(summary.totalDelays) },
		{
			label: "Total Delay Time",
			value: (
				moment.duration(summary.totalDurationSec, "seconds") as Duration
			).format("h[h] m[m] s[s]"),
		},
		{
			label: "Avg Delay / Event",
			value: (
				moment.duration(summary.avgDelayDurationSec, "seconds") as Duration
			).format("h[h] m[m] s[s]"),
		},
		{
			label: "Total Hours",
			value: `${(summary.totalDurationMin / 60).toFixed(1)} hrs`,
		},
	];

	return (
		<Paper variant="outlined" sx={{ mb: 1 }}>
			<Box
				sx={{
					px: 2,
					py: 1,
					backgroundColor: (theme) => theme.palette.primary.main,
				}}
			>
				<Typography variant="subtitle2" sx={{ color: "White" }}>
					{sectionName ? `${sectionName} — ` : ""}Delay Analysis Report
				</Typography>
			</Box>

			<Divider />

			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: "repeat(4, 1fr)",
					borderBottom: "1px solid",
					borderColor: "divider",
				}}
			>
				{kpis.map((kpi, i) => (
					<Box
						key={kpi.label}
						sx={{
							px: 2,
							py: 1.25,
							borderRight: i < kpis.length - 1 ? "1px solid" : "none",
							borderColor: "divider",
						}}
					>
						<Typography
							variant="caption"
							color="text.secondary"
							display="block"
						>
							{kpi.label}
						</Typography>
						<Typography variant="body1" fontWeight={700} color="primary">
							{kpi.value}
						</Typography>
					</Box>
				))}
			</Box>

			<Box sx={{ display: "flex" }}>
				<Box sx={{ flex: 1, borderRight: "1px solid", borderColor: "divider" }}>
					<Box
						sx={{
							px: 2,
							py: 0.75,
							minHeight: 36,
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							borderBottom: "1px solid",
							borderColor: "divider",
						}}
					>
						<Typography
							variant="caption"
							fontWeight={600}
							color="text.secondary"
							sx={{ textTransform: "uppercase" }}
						>
							Delays by Category
						</Typography>
					</Box>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Category</TableCell>
								<TableCell sx={{ width: "45%" }}>Duration</TableCell>
								<TableCell align="right">%</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{summary.byCategory.map((cat) => (
								<TableRow key={cat.name} hover>
									<TableCell>
										<Box
											sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
										>
											<Box
												sx={{
													width: 8,
													height: 8,
													borderRadius: "50%",
													backgroundColor: cat.color,
													flexShrink: 0,
												}}
											/>
											<Typography variant="body2">{cat.name}</Typography>
										</Box>
									</TableCell>
									<TableCell>
										<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
											<Typography
												variant="body2"
												fontWeight={500}
												sx={{ minWidth: 52 }}
											>
												{(
													moment.duration(
														cat.durationMin,
														"minutes",
													) as Duration
												).format("h[h] m[m]")}
											</Typography>
											<Box
												sx={{
													flex: 1,
													height: 4,
													borderRadius: 2,
													backgroundColor: "action.hover",
												}}
											>
												<Box
													sx={{
														height: "100%",
														width: `${(cat.durationMin / maxCategoryMin) * 100}%`,
														borderRadius: 2,
														backgroundColor: cat.color,
													}}
												/>
											</Box>
										</Box>
									</TableCell>
									<TableCell align="right">
										<Typography variant="body2" color="text.secondary">
											{cat.percentage.toFixed(1)}%
										</Typography>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Box>

				<Box sx={{ flex: 1 }}>
					<Box
						sx={{
							px: 2,
							py: 0.75,
							minHeight: 36,
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							borderBottom: "1px solid",
							borderColor: "divider",
						}}
					>
						<Typography
							variant="caption"
							fontWeight={600}
							color="text.secondary"
							sx={{ textTransform: "uppercase" }}
						>
							Top Delay Codes
						</Typography>
					</Box>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Delay Code</TableCell>
								<TableCell align="right">Time</TableCell>
								<TableCell align="right">Impact</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{summary.topDelayCodes.map((code) => (
								<TableRow key={code.name} hover>
									<TableCell>
										<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
											<Typography variant="body2">{code.name}</Typography>
										</Box>
									</TableCell>
									<TableCell align="right">
										<Typography variant="body2" fontWeight={600}>
											{(
												moment.duration(code.durationMin, "minutes") as Duration
											).format("h[h] m[m]")}
										</Typography>
									</TableCell>
									<TableCell align="right">
										<Typography
											variant="body2"
											fontWeight={600}
											color={code.percentage >= 10 ? "error" : "text.secondary"}
										>
											{code.percentage.toFixed(1)}%
										</Typography>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Box>
			</Box>
		</Paper>
	);
};
